import imageCompression from "browser-image-compression";

// typings
import {
  ContractClassification,
  EContractType,
  ECurrency,
  EFeePayee,
  EReturnPolicy,
  ESenderRole,
  EShipmentType,
  IContractDetailsBody,
  IContractDetailsResponseData,
  ItemDetailsDTO,
} from "../api/services/contract/models";
import {
  Broke,
  MilestonesParties,
} from "../../pages/home/newtransaction/addMilestonePriceForm";
import { browserName } from "./checkBrowserCompatible";

export const reverseMapping = (oldMap) =>
  Object.keys(oldMap).reduce(
    (key, value) =>
      Object.assign(key, {
        [oldMap[value]]: (key[oldMap[value]] || []).concat(value),
      }),
    {}
  );

export const typeMap = {
  General: ContractClassification.BASIC,
  Landing: ContractClassification.BASIC,
  Haraj: ContractClassification.BASIC_HARAJ,
  Stock: ContractClassification.STOCK_CONTRACT,
  Haraj_Stock: ContractClassification.STOCK_CONTRACT_HARAJ,
};

export const contractBody = (
  senderRole: ESenderRole,
  title: ItemDetailsDTO["title"],
  description: ItemDetailsDTO["description"],
  price: IContractDetailsBody["itemPrice"],
  shippingFee: IContractDetailsBody["shipment"]["fee"],
  // feePayee: EFeePayee,
  // inspectionPeriod: ItemDetailsDTO["inspectionPeriod"],
  // deliveryInDays: ItemDetailsDTO["deliveryInDays"],
  termsAccepted: boolean,
  shipmentType: EShipmentType,
  interactiveContract?: boolean,
  quantity?: number,
  tabbyAllowed?: boolean,
  category?: {
    id: ItemDetailsDTO["category"];
    name: string;
  },
  buyerCreatesContract?: boolean,
  contractType?: "General" | "Haraj" | "Landing" | "Stock" | "Haraj_Stock",
  sellerRequiresShippingInfo?: boolean
): IContractDetailsBody => {
  return {
    type: EContractType.QUICK_TRANSACTION,
    senderRole,
    itemDetail: {
      title: title,
      description: description,
      images: [],
    },
    itemPrice: price,
    currency: ECurrency.SAR,
    // feePayee: feePayee,
    // inspectionPeriod: inspectionPeriod,
    shipment: {
      type: shipmentType,
      fee: shippingFee,
      currency: ECurrency.SAR,
      // deliveryInDays: deliveryInDays,
    },
    returnPolicy: EReturnPolicy.NO_RETURN,
    returnFeePayee: EFeePayee.PROVIDER,
    receiverId: "",
    receiverName: "",
    waffyTermsAccepted: true,
    // interactiveContract: interactiveContract,
    quantity: quantity,
    contractClassification: typeMap[contractType || "General"],
    ...(category?.id
      ? {
          category: category?.id,
        }
      : {}),
    ...(tabbyAllowed || sellerRequiresShippingInfo
      ? {
          metaData: {
            ...(tabbyAllowed ? { tabbyAllowed } : {}),
            ...(sellerRequiresShippingInfo
              ? { sellerRequiresShippingInfo }
              : {}),
          },
        }
      : {}),
    ...(buyerCreatesContract
      ? {
          enableSubmittedStatus: buyerCreatesContract,
        }
      : {}),
  };
};

export const brokerContractBody = (
  title: ItemDetailsDTO["title"],
  description: ItemDetailsDTO["description"],
  price: IContractDetailsBody["itemPrice"],
  brokerFee: IContractDetailsBody["brokerFee"],
  termsAccepted: boolean,
  category?: {
    id: ItemDetailsDTO["category"];
    name: string;
  }
): Partial<IContractDetailsBody> => {
  return {
    type: EContractType.BROKERAGE_CONTRACT,
    senderRole: ESenderRole.BROKER,
    itemDetail: {
      title: title,
      description: description,
      images: [],
    },
    itemPrice: price,
    currency: ECurrency.SAR,
    returnPolicy: EReturnPolicy.NO_RETURN,
    returnFeePayee: EFeePayee.PROVIDER,
    receiverId: "",
    receiverName: "",
    brokerFee,
    waffyTermsAccepted: true,
    ...(category?.id
      ? {
          category: category?.id,
        }
      : {}),
  };
};

export const milestonesContractBody = (
  title: ItemDetailsDTO["title"],
  description: ItemDetailsDTO["description"],
  termsAccepted: boolean,
  milestones: {
    description: string;
    price: string;
    date: Date | null;
    sar?: number | undefined;
    id?: string;
    parties: {
      phoneNumber: string | number;
      role: ESenderRole;
      amount: string | number;
    }[];
  }[],
  category?: {
    id: ItemDetailsDTO["category"];
    name: string;
  },
  completeContract?: IContractDetailsResponseData
): {
  complexBody: Partial<IContractDetailsBody> | IContractDetailsBody;
  milestonesBody: Partial<IContractDetailsBody>[];
} => {
  return {
    complexBody: {
      ...(completeContract
        ? (completeContract as unknown as IContractDetailsBody)
        : {}),
      type: EContractType.COMPLEX_CONTRACT,
      senderRole: ESenderRole.PROVIDER,
      itemDetail: {
        title: title,
        description: description,
        images: [],
      },
      shipment: undefined,
      returnPolicy: EReturnPolicy.NO_RETURN,
      returnFeePayee: EFeePayee.PROVIDER,
      waffyTermsAccepted: true,
      ...(category?.id
        ? {
            category: category?.id,
          }
        : {}),
    },
    milestonesBody: milestones?.map((milestone) => ({
      ...(milestone?.id ? { id: milestone?.id } : {}),
      type: EContractType.MILESTONE_CONTRACT,
      senderRole: ESenderRole.PROVIDER,
      itemDetail: {
        title: milestone?.description,
        description: "",
      },
      itemPrice: milestone?.price,
      currency: ECurrency.SAR,
      parties: milestone?.parties,
      returnPolicy: EReturnPolicy.NO_RETURN,
      returnFeePayee: EFeePayee.PROVIDER,
      deadLine: milestone?.date?.toISOString(),
      waffyTermsAccepted: true,
    })),
  };
};

export const mapMilestonesData = (
  milestonesArray: IContractDetailsResponseData[]
) => {
  const parties = new Set<string>();
  const milestones: {
    description: string;
    price: string;
    date: Date | null;
    id?: string;
  }[] = [];
  const milestoneParties: MilestonesParties = {};
  let complexBroker: Broke | undefined = undefined;

  milestonesArray?.forEach((milestone, milestoneIndex) => {
    milestoneParties[milestoneIndex] = {
      pay: [],
      receive: [],
      ...(milestone?.parties?.some(
        (party) => party?.role === ESenderRole.BROKER && !party?.arbitrator
      )
        ? { broke: [] }
        : {}),
    };
    milestone?.parties
      ?.map((party) => {
        if (party?.role === ESenderRole.CUSTOMER) {
          milestoneParties[milestoneIndex]["pay"] = [
            ...(milestoneParties[milestoneIndex]["pay"] || []),
            {
              id: party?.phoneNumber || "",
              name: party?.phoneNumber || "",
              hasToPay: `${party?.amount || ""}`,
              value: party?.amount || 0,
            },
          ];
        } else if (party?.role === ESenderRole.PROVIDER) {
          milestoneParties[milestoneIndex]["receive"] = [
            ...milestoneParties[milestoneIndex]["receive"],
            {
              id: party?.phoneNumber || "",
              name: party?.phoneNumber || "",
              hasToReceive: `${party?.amount || ""}`,
              sar: party?.fee || 0,
              willReceive: +(party?.amount || 0) - +(party?.fee || 0),
            },
          ];
        } else if (party?.role === ESenderRole.BROKER) {
          if (party?.arbitrator) {
            complexBroker = {
              id: party?.phoneNumber || "",
              name: party?.phoneNumber || "",
              hasToReceive: {
                ...(complexBroker?.hasToReceive || {}),
                [milestoneIndex]: `${party?.amount || ""}`,
              },
              arbitrator: party?.arbitrator || true,
            };
          } else {
            milestoneParties[milestoneIndex]["broke"] = [
              ...(milestoneParties[milestoneIndex]["broke"] || []),
              {
                id: party?.phoneNumber || "",
                name: party?.phoneNumber || "",
                hasToReceive: `${party?.amount || ""}`,
                arbitrator: party?.arbitrator || false,
              },
            ];
          }
        }

        return party?.phoneNumber;
      })
      ?.forEach((party) => parties.add(party || ""));

    milestones.push({
      id: milestone?.id,
      date: new Date(milestone?.deadLine || ""),
      description: milestone?.itemDetail?.title,
      price: `${milestone?.itemPrice}`,
    });
  });

  return {
    parties: Array.from(parties),
    milestones,
    milestoneParties,
    complexBroker,
  };
};

export const reviseParties = (
  milestoneParties: MilestonesParties | undefined,
  parties: string[]
) => {
  if (milestoneParties) {
    for (const [milestoneIndex, milestonePartiesList] of Object.entries(
      milestoneParties
    )) {
      const newPayingParties = milestonePartiesList?.pay?.filter((party) => {
        return parties?.includes(`${party?.id}`);
      });

      milestoneParties[milestoneIndex]["pay"] = [...newPayingParties];

      const newReceivingParties = milestonePartiesList?.receive?.filter(
        (party) => {
          return parties?.includes(`${party?.id}`);
        }
      );

      milestoneParties[milestoneIndex]["receive"] = [...newReceivingParties];

      if (
        milestonePartiesList?.broke &&
        milestonePartiesList?.broke?.length > 0
      ) {
        const newBrokingParties = milestonePartiesList?.broke?.filter(
          (party) => {
            return parties?.includes(`${party?.id}`);
          }
        );

        milestoneParties[milestoneIndex]["broke"] = [
          ...(parties?.length > 2 ? newBrokingParties || [] : []),
        ];
      }
    }
  }

  return milestoneParties;
};

export const handleCompressImage = async (
  imageFile: File | undefined
): Promise<File | void> => {
  if (!imageFile) return;

  const options = {
    maxSizeMB: 2,
    maxWidthOrHeight: 1920,
    useWebWorker: browserName() === "Safari" ? false : true,
  };

  try {
    const compressedFile = await imageCompression(imageFile, options);
    return compressedFile;
  } catch (error) {
    console.log(error);
    return;
  }
};

export const validateDate = (
  value: React.KeyboardEvent<HTMLTextAreaElement>["key"]
) => {
  if (/[٠-٩]/.test(value)) return true;
  if (/[0-9]/.test(value)) return true;
  if (value === "-" || value === "/") return true;
  return false;
};

export const isValidDateFormat = (inputString: string) => {
  // Regular expression to match either "dd-mm-yyyy" or "dd/mm/yyyy" format
  const dateFormatRegex =
    /^(0[1-9]|[12][0-9]|3[01])[-\/](0[1-9]|1[0-2])[-\/]\d{4}$/;

  return dateFormatRegex.test(inputString);
};

export const convertToArabic = (englishNumber) => {
  const arabicDigits = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];

  return String(englishNumber).replace(/\d/g, (digit) => arabicDigits[digit]);
};

export const configureNumber = (number) => {
  const arabicNumbers = [
    /٠/g,
    /١/g,
    /٢/g,
    /٣/g,
    /٤/g,
    /٥/g,
    /٦/g,
    /٧/g,
    /٨/g,
    /٩/g,
  ];
  if (typeof number === "string") {
    for (let i = 0; i < arabicNumbers.length; i++) {
      number = number.replace(arabicNumbers[i], i);
    }
  }
  return number;
};

export const convertDate = (date: string) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("ar-eg");
};

export const formatDateToArabic = (isoDateString?: string): string => {
  if (!isoDateString) return "";
  // Months names in Arabic
  const arabicMonths = [
    "يناير",
    "فبراير",
    "مارس",
    "أبريل",
    "مايو",
    "يونيو",
    "يوليو",
    "أغسطس",
    "سبتمبر",
    "أكتوبر",
    "نوفمبر",
    "ديسمبر",
  ];

  // Days names in Arabic
  const arabicDays = [
    "الأحد",
    "الإثنين",
    "الثلاثاء",
    "الأربعاء",
    "الخميس",
    "الجمعة",
    "السبت",
  ];

  // Parse ISO date string
  const date = new Date(isoDateString);

  // Get day, month, and year
  const day = date.getDate();
  const month = date.getMonth();

  // Get day name and month name in Arabic
  const dayName = arabicDays[date.getDay()];
  const monthName = arabicMonths[month];

  // Construct the formatted date string
  const formattedDate = `${dayName} ${day} ${monthName}`;

  return formattedDate;
};

type DebounceFunction<T extends (...args: any[]) => any> = (
  ...args: Parameters<T>
) => void;

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): DebounceFunction<T> {
  let timeout: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export const validateNumber = (
  value: React.KeyboardEvent<HTMLTextAreaElement>["key"]
) => {
  if (/[0-9]/.test(value)) return true;
  return false;
};

export const calculateExpiryDate = (expiresIn: number) => {
  const time = new Date();
  time.setSeconds(time.getSeconds() + expiresIn);
  return time.toISOString();
};

// Utility function to safely parse JSON
export const parseJSONFunc = (value: string | null): any => {
  try {
    return value ? JSON.parse(value) : null;
  } catch (error) {
    return value; // Return the original value if it's not JSON
  }
};

export const ESCLATION_REASONS = [
  {
    key: "product_not_delivered",
    value: "lbl_fill_escalation_donot_receive_product",
  },
  {
    key: "product_not_identical_with_description",
    value: "lbl_fill_escalation_product_not_match_desc",
  },
  { key: "product_not_working", value: "lbl_fill_escalation_product_not_work" },
  {
    key: "product_has_cracks_and_defects",
    value: "lbl_fill_escalation_product_is_damged",
  },
];
